import React, { useEffect, useState } from "react";
import {
  Box, Typography, Stack, Paper, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Switch, Alert,
} from "@mui/material";
import { LocalOffer, Add, Edit, Delete, Visibility, VisibilityOff } from "@mui/icons-material";
import { couponsAPI, Coupon, CouponPayload } from "../../services/adminApi";

const NAVY = "#0A1628";
const BLUE = "#1565C0";

const DEFAULT_FORM: CouponPayload = {
  code: "",
  type: "percent",
  value: 10,
  active: true,
  expiresAt: "",
  maxUses: undefined,
};

const AdminMarketingCoupons: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CouponPayload>({ ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    couponsAPI.list()
      .then((res) => setCoupons(res.data?.coupons ?? []))
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...DEFAULT_FORM });
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (c: Coupon) => {
    setEditingId(c._id);
    setForm({
      code: c.code,
      type: c.type,
      value: c.value,
      active: c.active,
      expiresAt: c.expiresAt ?? "",
      maxUses: c.maxUses,
      applicablePlans: c.applicablePlans,
    });
    setFormError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setFormError("Code is required"); return; }
    if (!form.value || form.value <= 0) { setFormError("Enter a discount value greater than 0"); return; }
    setSaving(true);
    setFormError("");
    try {
      const payload: CouponPayload = { ...form, code: form.code.trim().toUpperCase() };
      if (editingId) {
        const res = await couponsAPI.update(editingId, payload);
        setCoupons((prev) => prev.map((c) => (c._id === editingId ? res.data.coupon : c)));
      } else {
        const res = await couponsAPI.create(payload);
        setCoupons((prev) => [...prev, res.data.coupon]);
      }
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err?.response?.data?.message || "Save failed — coupon backend may not be wired up yet");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (c: Coupon) => {
    try {
      const res = await couponsAPI.update(c._id, { active: !c.active });
      setCoupons((prev) => prev.map((x) => (x._id === c._id ? res.data.coupon : x)));
    } catch {
      setError("Failed to update coupon");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await couponsAPI.remove(deleteId);
      setCoupons((prev) => prev.filter((c) => c._id !== deleteId));
      setDeleteId(null);
    } catch {
      setError("Failed to delete coupon");
    } finally {
      setDeleting(false);
    }
  };

  const formatDiscount = (c: Coupon) => c.type === "percent" ? `${c.value}% off` : `$${c.value} off`;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>

      {/* ── Page Hero ── */}
      <Box sx={{
        mb: 4, p: { xs: 2.5, md: 3.5 }, borderRadius: "20px",
        background: "linear-gradient(135deg,#0D47A1 0%,#1565C0 55%,#1976D2 100%)",
        boxShadow: "0 8px 32px rgba(21,101,192,.3)",
      }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <LocalOffer sx={{ color: "#fff", fontSize: "1.6rem" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>
                Coupons
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,.7)", mt: 0.3 }}>
                Create discount codes for checkout — percent or flat amount off
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip label={`${coupons.length} coupon${coupons.length === 1 ? "" : "s"}`} size="small" sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff", fontWeight: 600, fontSize: ".72rem" }} />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreate}
              sx={{ bgcolor: "#fff", color: BLUE, fontWeight: 700, textTransform: "none", borderRadius: "10px", "&:hover": { bgcolor: "#F0F7FF" } }}
            >
              New Coupon
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }} onClose={() => setError("")}>{error}</Alert>}

      <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
        <Box sx={{ px: 2.5, py: 2, bgcolor: "#0A1628" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Coupon Codes</Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,.5)" }}>Discounted price is always re-validated server-side at checkout</Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} sx={{ color: "#1565C0" }} />
          </Box>
        ) : coupons.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center", color: "#78909C" }}>
            <LocalOffer sx={{ fontSize: 48, color: "#BBDEFB", mb: 1 }} />
            <Typography variant="body2">No coupons yet.</Typography>
            <Typography variant="caption">Create your first discount code to use it at checkout.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Discount</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Uses</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Expires</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#0A1628" }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coupons.map((c) => (
                  <TableRow key={c._id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: ".88rem", color: "#0A1628", letterSpacing: "0.04em" }}>{c.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "#455A64" }}>{formatDiscount(c)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.active ? "Active" : "Inactive"}
                        sx={{ bgcolor: c.active ? "#E8F5E9" : "#F5F5F5", color: c.active ? "#2E7D32" : "#9E9E9E", fontWeight: 700, fontSize: ".68rem" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "#455A64" }}>
                        {c.usedCount}{c.maxUses ? ` / ${c.maxUses}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "#455A64" }}>
                        {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={c.active ? "Deactivate" : "Activate"} arrow>
                        <IconButton size="small" onClick={() => handleToggleActive(c)} sx={{ color: c.active ? "#10B981" : "#9CA3AF" }}>
                          {c.active ? <Visibility sx={{ fontSize: 17 }} /> : <VisibilityOff sx={{ fontSize: 17 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" onClick={() => openEdit(c)} sx={{ color: BLUE }}>
                          <Edit sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton size="small" onClick={() => setDeleteId(c._id)} sx={{ color: "#EF4444", opacity: 0.7, "&:hover": { opacity: 1 } }}>
                          <Delete sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingId ? "Edit Coupon" : "New Coupon"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.2} sx={{ mt: 0.5 }}>
            {formError && <Alert severity="error" sx={{ borderRadius: "10px" }}>{formError}</Alert>}

            <TextField
              fullWidth size="small" label="Code *"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="e.g. WELCOME10"
              inputProps={{ maxLength: 30, style: { textTransform: "uppercase" } }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            <Stack direction="row" spacing={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.type}
                  label="Type"
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as "percent" | "flat" }))}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="percent">Percent off</MenuItem>
                  <MenuItem value="flat">Flat amount off</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth size="small" type="number" label={form.type === "percent" ? "Value (%)" : "Value ($)"}
                value={form.value}
                onChange={(e) => setForm((f) => ({ ...f, value: Number(e.target.value) }))}
                inputProps={{ min: 1, max: form.type === "percent" ? 100 : undefined }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
            </Stack>

            <TextField
              fullWidth size="small" type="date" label="Expires (optional)"
              value={form.expiresAt ? form.expiresAt.slice(0, 10) : ""}
              onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            <TextField
              fullWidth size="small" type="number" label="Max uses (optional)"
              value={form.maxUses ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, maxUses: e.target.value ? Number(e.target.value) : undefined }))}
              inputProps={{ min: 1 }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.active ?? true}
                  onChange={(_, v) => setForm((f) => ({ ...f, active: v }))}
                />
              }
              label={<Typography variant="body2" fontWeight={600}>{form.active ? "Active" : "Inactive"}</Typography>}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: "none", borderRadius: "8px" }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700, bgcolor: BLUE, "&:hover": { bgcolor: "#0D47A1" }, minWidth: 100 }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: "#FFF" }} /> : editingId ? "Save Changes" : "Create Coupon"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog open={!!deleteId} onClose={() => !deleting && setDeleteId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle fontWeight={700}>Delete Coupon</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">Are you sure? This coupon code will stop working immediately.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteId(null)} disabled={deleting} sx={{ textTransform: "none", borderRadius: "8px" }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleDelete} disabled={deleting}
            sx={{ textTransform: "none", borderRadius: "8px", bgcolor: "#EF4444", "&:hover": { bgcolor: "#DC2626" }, minWidth: 90 }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMarketingCoupons;
